import { useTranslation } from 'react-i18next';

import { RatingStars } from '@/components/atoms/RatingStars';
import { ScoreBadge } from '@/components/atoms/ScoreBadge';

import { DISTRIBUTION_KEYS, EXTERNAL_SCORE_MAX } from './Quality.constants';
import { styles } from './Quality.styles';
import type { ReportDistributionProps, ReportScoreCardsProps } from './Quality.types';
import { computeResolutionScore, distributionBarPct, formatAverage } from './Quality.utils';

export const ReportScoreCards = ({ latest }: ReportScoreCardsProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });
	const conversations = latest?.conversationsTotal ?? 0;

	return (
		<div className={styles.scoreGrid}>
			<article className={styles.scoreCard}>
				<p className={styles.scoreLabel}>{t('LLM_SCORE_LABEL')}</p>
				<div className={styles.scoreValue}>
					<ScoreBadge score={latest?.llmScore ?? null} size="lg" />
				</div>
				<p className={styles.scoreSub}>
					{t('VERDICTS_SUMMARY', { passing: latest?.llmPassing ?? 0, total: latest?.llmTotal ?? 0 })}
				</p>
			</article>
			<article className={styles.scoreCard}>
				<p className={styles.scoreLabel}>{t('EXTERNAL_SCORE_LABEL')}</p>
				<div className={styles.scoreValue}>
					<span className={styles.scoreAverage}>{formatAverage(latest?.externalAverage)}</span>
					{latest?.externalAverage ? (
						<span className={styles.scoreMax}>{t('EXTERNAL_SCORE_MAX_SUFFIX', { max: EXTERNAL_SCORE_MAX })}</span>
					) : null}
				</div>
				<p className={styles.scoreSub}>
					{t('RATINGS_SUMMARY', { count: latest?.externalTotal ?? 0, passing: latest?.externalPassing ?? 0 })}
				</p>
			</article>
			<article className={styles.scoreCard}>
				<p className={styles.scoreLabel}>{t('RESOLUTION_LABEL')}</p>
				<div className={styles.scoreValue}>
					<ScoreBadge score={computeResolutionScore(latest)} size="lg" />
				</div>
				<p className={styles.scoreSub}>
					{t('RESOLUTION_SUMMARY', { closed: latest?.closedTotal ?? 0, total: conversations })}
				</p>
				<p className={styles.scoreSub}>
					{t('HANDOFFS_SUMMARY', { count: latest?.handoffTotal ?? 0, total: conversations })}
				</p>
			</article>
		</div>
	);
};

export const ReportDistribution = ({ report }: ReportDistributionProps) => {
	const { t } = useTranslation('translation', { keyPrefix: 'reports' });
	const max = Math.max(1, ...Object.values(report.externalDistribution));

	return (
		<section className={styles.distribution}>
			<header className={styles.distributionHeader}>
				<p className={styles.distributionTitle}>{t('DISTRIBUTION_TITLE')}</p>
				<RatingStars value={Math.round(report.externalAverage ?? 0)} size="sm" />
			</header>
			<div className={styles.distributionBars}>
				{DISTRIBUTION_KEYS.map((stars) => {
					const count = report.externalDistribution[stars] ?? 0;

					return (
						<div key={stars} className={styles.distributionColumn}>
							<div className={styles.distributionTrack}>
								<div
									className={styles.distributionBar}
									style={{ height: `${distributionBarPct(count, max)}%` }}
									aria-hidden
								/>
							</div>
							<span className={styles.distributionStar}>{t('DISTRIBUTION_STAR', { stars })}</span>
							<span className={styles.distributionCount}>{count}</span>
						</div>
					);
				})}
			</div>
		</section>
	);
};
